import { z } from 'zod';
import { validarMotivoSolicitacao } from './solicitacao.motivo';
import { TIPOS_SOLICITACAO_SEM_CLIENTE } from './solicitacao.types';

const TipoSolicitacaoEnum = z.enum([
  'admissao',
  'desligamento',
  'mudanca_projeto',
  'nova_demanda',
  'troca_equipamento',
  'devolucao',
  'pedido_colaborador',
  'formatacao',
  'acessos',
  'divergencia_inventario',
]);

const StatusSolicitacaoEnum = z.enum(['aberta', 'em_andamento', 'concluida', 'cancelada', 'excluida']);

const MotivoExclusaoTipoEnum = z.enum(['duplicidade', 'nao_necessaria', 'aberta_incorretamente', 'outro']);

const StatusAcessoEnum = z.enum(['pendente', 'concluido', 'naoAplicavel']);

const AcaoAcessoEnum = z.enum(['criar', 'revogar', 'resetar_senha']);

const AuditoriaAtorSchema = z.object({
  id: z.string().min(1, 'O ID do ator é obrigatório'),
  name: z.string().min(1, 'O nome do ator é obrigatório'),
  avatar: z.string().nullable().optional(),
});

const SolicitacaoAcessoSchema = z.object({
  sistema: z.string().min(1, 'O sistema do acesso é obrigatório'),
  acao: AcaoAcessoEnum,
  status: StatusAcessoEnum,
  concluidoPor: AuditoriaAtorSchema.nullable().optional(),
  concluidoEm: z.any().nullable().optional(), // Timestamp do Firestore
});

const SolicitacaoKitSchema = z.object({
  solicitado: z.boolean(),
  enviadoJuntoComEquipamento: z.boolean(),
  enviadoPeloRH: z.boolean(),
  dataEnvio: z.any().nullable().optional(),
});

const ColaboradorPendenteSchema = z.object({
  nome: z.string().min(1, 'O nome é obrigatório'),
  emailInformado: z.string().email('Formato de e-mail inválido'),
  positionId: z.string().nullable().optional(),
  positionName: z.string().nullable().optional(),
  seniorityId: z.string().nullable().optional(),
  seniorityName: z.string().nullable().optional(),
});

/**
 * Schema de criação. `solicitanteId`/`solicitanteEmail` devem corresponder ao usuário
 * autenticado que está criando o documento — a validação de que isso é verdade é feita
 * em firestore.rules (get() comparando e-mail), não neste schema.
 *
 * `colaboradorId` só pode ser null quando `tipoSolicitacao === 'admissao'` e
 * `colaboradorPendente` está presente (pessoa ainda não cadastrada no Radar) — para todos
 * os demais tipos, um colaborador real já cadastrado continua obrigatório.
 */
export const SolicitacaoCreateSchema = z
  .object({
    tipoSolicitacao: TipoSolicitacaoEnum,
    colaboradorId: z.string().min(1).nullable(),
    colaboradorEmail: z.string().email('Formato de e-mail inválido'),
    colaboradorNome: z.string().min(1, 'O nome do colaborador é obrigatório'),
    colaboradorPendente: ColaboradorPendenteSchema.nullable().optional(),
    solicitanteId: z.string().min(1, 'O solicitante é obrigatório'),
    solicitanteEmail: z.string().email('Formato de e-mail inválido'),
    gestorEmailNoMomentoDaCriacao: z.string().email('Formato de e-mail inválido').nullable().optional(),
    alocacaoReferenciadaId: z.string().nullable().optional(),
    alocacaoResponsavelId: z.string().nullable().optional(),
    alocacaoResponsavelNome: z.string().nullable().optional(),
    // Null só pra 'acessos' (pedido pontual de sistema interno, não amarrado a
    // projeto/cliente) — refine abaixo exige preenchido pra todos os demais tipos.
    clienteId: z.string().min(1, 'O cliente é obrigatório').nullable(),
    observacoes: z.string().max(2000, 'Observações devem ter no máximo 2000 caracteres').optional(),
    // Troca de Equipamento, Devolução e Periféricos/Acessórios — ver solicitacao.motivo.ts.
    motivoSolicitacao: z.string().nullable().optional(),
    motivoSolicitacaoDetalhe: z.string().max(1000).nullable().optional(),
    // Só usado pelo tipo 'acessos' — sistema/ação escolhidos por quem abre (ver
    // NovaSolicitacaoContent), 1 único item, sempre 'pendente' na criação.
    acessos: z.array(SolicitacaoAcessoSchema).optional(),
    // Bloco "Dados para o cliente" (ver emailCliente.modelos.ts).
    dadosCliente: z.record(z.string().max(500)).nullable().optional(),
  })
  .refine(
    (data) => data.tipoSolicitacao === 'admissao' || data.colaboradorId !== null,
    { message: 'O colaborador é obrigatório para este tipo de solicitação', path: ['colaboradorId'] }
  )
  .refine(
    (data) => TIPOS_SOLICITACAO_SEM_CLIENTE.includes(data.tipoSolicitacao) || data.clienteId !== null,
    { message: 'O cliente é obrigatório para este tipo de solicitação', path: ['clienteId'] }
  )
  .refine(
    (data) => data.colaboradorId !== null || data.colaboradorPendente != null,
    { message: 'Informe nome e e-mail do futuro colaborador', path: ['colaboradorPendente'] }
  )
  .refine(
    (data) => validarMotivoSolicitacao(data.tipoSolicitacao, data.motivoSolicitacao, data.motivoSolicitacaoDetalhe) === null,
    { message: 'Informe o motivo da solicitação', path: ['motivoSolicitacao'] }
  );

/** Campos de dados/contexto — editáveis por SI, RH e Solicitante. */
export const SolicitacaoUpdateSchema = z.object({
  id: z.string().min(1, 'O ID da solicitação é obrigatório'),
  observacoes: z.string().max(2000, 'Observações devem ter no máximo 2000 caracteres').optional(),
  motivoSolicitacao: z.string().nullable().optional(),
  motivoSolicitacaoDetalhe: z.string().max(1000).nullable().optional(),
  clienteId: z.string().min(1, 'O cliente é obrigatório').optional(),
  tipoSolicitacao: TipoSolicitacaoEnum.optional(),
});

/** "Vincular Colaborador" — só a transição colaboradorId null → id real. */
export const SolicitacaoVincularColaboradorSchema = z.object({
  id: z.string().min(1, 'O ID da solicitação é obrigatório'),
  colaboradorId: z.string().min(1, 'O colaborador é obrigatório'),
  colaboradorEmail: z.string().email('Formato de e-mail inválido'),
  colaboradorNome: z.string().min(1, 'O nome do colaborador é obrigatório'),
  updatedBy: AuditoriaAtorSchema,
});

/** Campos operacionais — exclusivos do SI. */
export const SolicitacaoOperacionalUpdateSchema = z.object({
  id: z.string().min(1, 'O ID da solicitação é obrigatório'),
  alocacaoReferenciadaId: z.string().nullable().optional(),
  alocacaoResponsavelId: z.string().nullable().optional(),
  alocacaoResponsavelNome: z.string().nullable().optional(),
  acessos: z.array(SolicitacaoAcessoSchema).optional(),
  kit: SolicitacaoKitSchema.nullable().optional(),
  solicitacaoRelacionadaId: z.string().nullable().optional(),
});

export const SolicitacaoCancelamentoSchema = z.object({
  id: z.string().min(1, 'O ID da solicitação é obrigatório'),
  canceledBy: AuditoriaAtorSchema,
  motivoCancelamento: z.string().trim().min(1, 'Informe o motivo do cancelamento').max(1000),
});

export const SolicitacaoReativacaoSchema = z.object({
  id: z.string().min(1, 'O ID da solicitação é obrigatório'),
});

export const SolicitacaoExclusaoSchema = z
  .object({
    id: z.string().min(1, 'O ID da solicitação é obrigatório'),
    excludedBy: AuditoriaAtorSchema,
    motivoExclusaoTipo: MotivoExclusaoTipoEnum,
    motivoExclusao: z.string().trim().max(1000).nullable().optional(),
    solicitacaoRelacionadaId: z.string().nullable().optional(),
  })
  .refine((data) => data.motivoExclusaoTipo !== 'outro' || !!data.motivoExclusao?.trim(), {
    message: 'Descreva o motivo da exclusão',
    path: ['motivoExclusao'],
  })
  .refine((data) => data.motivoExclusaoTipo !== 'duplicidade' || !!data.solicitacaoRelacionadaId, {
    message: 'Informe a solicitação duplicada',
    path: ['solicitacaoRelacionadaId'],
  });

/** Schema completo — usado para validação de leitura/integridade, não para formulários. */
export const SolicitacaoSchema = z.object({
  id: z.string().min(1),
  tipoSolicitacao: TipoSolicitacaoEnum,
  status: StatusSolicitacaoEnum,
  colaboradorId: z.string().min(1).nullable(),
  colaboradorEmail: z.string().email(),
  colaboradorNome: z.string().min(1),
  colaboradorPendente: ColaboradorPendenteSchema.nullable().optional(),
  solicitanteId: z.string().min(1),
  solicitanteEmail: z.string().email(),
  gestorEmailNoMomentoDaCriacao: z.string().email().nullable().optional(),
  alocacaoReferenciadaId: z.string().nullable().optional(),
  alocacaoResponsavelId: z.string().nullable().optional(),
  alocacaoResponsavelNome: z.string().nullable().optional(),
  clienteId: z.string().min(1).nullable(),
  dataAbertura: z.any(),
  dataConclusao: z.any().nullable().optional(),
  acessos: z.array(SolicitacaoAcessoSchema).optional(),
  kit: SolicitacaoKitSchema.nullable().optional(),
  solicitacaoOrigemId: z.string().nullable().optional(),
  solicitacaoRelacionadaId: z.string().nullable().optional(),
  observacoes: z.string().optional(),
  motivoSolicitacao: z.string().nullable().optional(),
  motivoSolicitacaoDetalhe: z.string().nullable().optional(),
  createdBy: AuditoriaAtorSchema,
  updatedBy: AuditoriaAtorSchema.nullable().optional(),
  canceledBy: AuditoriaAtorSchema.nullable().optional(),
  motivoCancelamento: z.string().nullable().optional(),
  excludedBy: AuditoriaAtorSchema.nullable().optional(),
  motivoExclusaoTipo: MotivoExclusaoTipoEnum.nullable().optional(),
  motivoExclusao: z.string().nullable().optional(),
  createdAt: z.any(),
  updatedAt: z.any(),
});
